import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, ScrollText } from "lucide-react";

export default function AdminRaffle() {
  const [raffles, setRaffles] = useState([]);
  const [entries, setEntries] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drawing, setDrawing] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/gamification/raffles");
    setRaffles(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const view = async (period) => {
    setSelected(period);
    const r = await api.get(`/admin/gamification/raffle/${period}/entries`);
    setEntries(r.data);
  };

  const draw = async (period) => {
    if (!window.confirm(`Draw the winner for ${period}? This is irreversible.`)) return;
    setDrawing(true);
    try {
      const { data } = await api.post(`/admin/gamification/raffle/${period}/draw`);
      toast.success(`Winner: ${data.winner_name} (${data.winner_email})`, { description: `Seed: ${data.seed}` });
      await load();
      if (selected === period) await view(period);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draw failed");
    } finally {
      setDrawing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-raffle-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
          <Trophy className="w-6 h-6 text-amber-500" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold">Raffle admin</h1>
          <p className="text-sm text-muted-foreground">Draw monthly raffle winners with verifiable seed. Auditable.</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>All raffles</CardTitle></CardHeader>
        <CardContent>
          {raffles.length === 0 ? (
            <div className="text-sm text-muted-foreground">No raffles yet — customers must enter to create the first one.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground text-left">
                <tr><th className="p-2">Period</th><th>Prize</th><th>Entries</th><th>Status</th><th>Winner</th><th></th></tr>
              </thead>
              <tbody>
                {raffles.map(r => (
                  <tr key={r.id} className="border-t border-border" data-testid={`raffle-row-${r.id}`}>
                    <td className="p-2 font-mono">{r.period}</td>
                    <td>{r.prize_label}</td>
                    <td>{r.entries_count}</td>
                    <td><Badge variant={r.status === "drawn" ? "default" : "outline"}>{r.status}</Badge></td>
                    <td className="text-xs">{r.winner_user_id ? r.winner_user_id.slice(0, 8) : "—"}</td>
                    <td className="space-x-2">
                      <Button size="sm" variant="ghost" onClick={() => view(r.period)} data-testid={`view-${r.id}`}>
                        <ScrollText className="w-3 h-3 mr-1" /> Entries
                      </Button>
                      {r.status !== "drawn" && r.entries_count > 0 && (
                        <Button size="sm" onClick={() => draw(r.period)} disabled={drawing} data-testid={`draw-${r.id}`}>
                          Draw
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEntries(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Entries — {selected}</DialogTitle></DialogHeader>
          {entries === null ? <div className="text-muted-foreground">Loading…</div> : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground text-left"><th className="p-2">#</th><th>Name</th><th>Email</th><th>Source</th><th>At</th></tr></thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="p-2 font-mono">{i + 1}</td>
                      <td>{e.user_name}</td>
                      <td className="text-xs">{e.user_email}</td>
                      <td><Badge variant="outline" className="text-[10px]">{e.source}</Badge></td>
                      <td className="text-xs">{new Date(e.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
